
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Stack,
    IconButton,
    Menu,
    Checkbox,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    InputAdornment,
    Popper,
    styled,
    alpha,
    MenuItem,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { IndeterminateCheckBox as IndeterminateCheckBoxIcon } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import CostBreakdownTable from './CostBreakdownTable';
import CostOverTimeChart from './CostOverTimeChart';
import SuccessToast from '../../SuccessToast';
import SummaryCard from './SummaryCard';
import SaveFilterDialog from './SaveFilterDialog';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { TimeSeriesDataPoint, TimeSeriesGroup } from './CostOverTimeChart';
import { BreakdownRow } from './CostBreakdownTable';
import { createColorMap } from '../../utils/colorUtils';
import { useNavigate } from 'react-router-dom';
import LabelFilterMenu, { LabelFilterPair } from './LabelFilterMenu';
import { CustomPickersDay } from './calendarStyles';
import { useCostAnalysisContext } from './CostAnalysisContext'; 

// --- INTERFACES (DEFINED ONCE AT THE TOP FOR SINGLE-FILE STRUCTURE) ---
export interface DataSourceOption {
    id: string;
    name: string;
    providerType: string;
    isSyncing: boolean;
}

export interface FilterOptions {
    services: string[];
    regions: string[];
    projectIds: string[];
    skus: string[];
    labelKeys: string[];
    dataSources: DataSourceOption[];
}

export interface KpiData {
  totalCost: number;
  totalCostChangePercentage: number | null;
  totalForecastedCost: number;
  totalForecastedCostChangePercentage: number | null;
  topGroup: string | null;
  topGroupCost: number;
  topGroupCredits: number;
  totalCredits: number;
  topGroupCostChangePercentage: number | null;
}

// This defines the shape of the data INSIDE the 'getCostAnalysis' object
export interface CostAnalysisResult {
  kpis: KpiData;
  timeSeries: TimeSeriesDataPoint[];
  breakdown: BreakdownRow[];
}

// This defines the shape of the ENTIRE payload returned by the GraphQL query.
// This is the type we will actually use for our preloaded data.
export interface CostAnalysisPayload {
  getCostAnalysis: CostAnalysisResult;
}

const EmptyStateOverlay: React.FC<{ onNavigate: () => void, userRole?: string | null }> = ({ onNavigate, userRole }) => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        p: 2,
        textAlign: 'center',
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ml: 12
      }}>
        <img src="/icons/No-data-src-added-log.svg" alt="No Data" style={{ width: '150px', height: '150px', marginBottom: '12px' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 , fontFamily:'Inter'}}>
            Ready to Analyze Your Costs?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb:2, maxWidth: '400px',fontFamily:'Inter' }}>
            Please add a data source to your tenant to visualize spending, identify trends, and start saving.
        </Typography>
        {userRole !== 'viewer' && (
          <Button disableElevation variant="contained" onClick={onNavigate} sx={{fontFamily:'Inter', backgroundColor:'#0064FF', fontSize: '14px', fontWeight: 500, borderRadius: '8px', textTransform: 'none' }}>
              Add New Data Source
          </Button>
        )}
      </Box>
    </Box>
);

const NoTenantEmptyState: React.FC = () => (
    <Box
      sx={{
        // This outer box centers the white box on the page
        background: '#F9F9F9', 
        width: '100%',
        minHeight: 'calc(100vh - 100px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Box sx={{
        // This inner box styles the white container and centers its content
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        p: { xs: 4, md: 6 },
        display: 'flex',
        flexDirection: 'column',
        // **THIS IS THE FIX**: This property centers the content vertically
        justifyContent: 'center',
        // This property centers the content horizontally
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: '2000px',
        width: '100%',
        height:'690px',
        marginTop:"10px"
      }}>
        <img 
          src="/icons/no-cost-analysis.svg" 
          alt="No Tenant Selected" 
          style={{ width: '143px', height: '143px', marginBottom: '10px' }} 
        />
        <Typography 
          variant="h6" 
          sx={{ fontWeight: 600, mb: 1, fontFamily:'Inter' }}
        >
            No Tenant Added
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 1, maxWidth: '400px', fontFamily:'Inter' }}
        >
            It looks like you haven't set up a tenant yet. Please add a tenant to begin cost analysis.
        </Typography>
      </Box>
    </Box>
);

// --- NEW REUSABLE COMPONENT for messages inside the content area ---
const CenteredMessage: React.FC<{ imgSrc: string; title: string; message: string; }> = ({ imgSrc, title, message }) => (
    <Paper
        elevation={0}
        sx={{
            // border: '2px solid #007BFF',
            borderRadius: '16px',
            p: 3,
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            backgroundColor: '#FFFFFF',
        }}
    >
        <img
            src={imgSrc}
            alt={title}
            style={{ width: '150px', height: '150px', marginBottom: '24px' }}
        />
        <Typography sx={{ fontWeight: 600,fontsize:'16px', mb: 1, fontFamily: 'Inter' }}>
            {title}
        </Typography>
        <Typography sx={{ fontFamily: 'Inter', maxWidth: '550px', textAlign: 'center',fontWeight: 400,fontsize:'10px',color:"#666666" }}>
            {message}
        </Typography>
    </Paper>
);

export enum CostAnalysisGroupBy {
  service = 'service',
  projectId = 'projectId',
  region = 'region',
  dataSourceId = 'dataSourceId',
  sku = 'sku'
}

export interface LabelFilter {
    key: string;
    value: string;
}

export interface CostAnalysisInput {
  tenantId: string;
  dateRange: {
    start: string;
    end: string;
  };
  filters?: {
    dataSourceIds?: string[];
    services?: string[];
    regions?: string[];
    projectIds?: string[];
    skus?: string[];
    labels?: LabelFilter[];
  };
  groupBy: CostAnalysisGroupBy;
}

const GET_COST_ANALYSIS = gql`
  query GetCostAnalysis($input: CostAnalysisInput!) {
    getCostAnalysis(input: $input) {
      kpis {
        totalCost
        totalCostChangePercentage
        totalForecastedCost
        totalForecastedCostChangePercentage
        topGroup
        topGroupCost
        topGroupCredits
        totalCredits
        topGroupCostChangePercentage
      }
      timeSeries {
        date
        groups {
          name
          cost
        }
      }
      breakdown {
        group
        cost
        credits
        discount
        usage
        totalCost
      }
    }
  }
`;

const GET_FILTER_OPTIONS = gql`
  query GetFilterOptions($input: FilterOptionsInput!) {
    getFilterOptions(input: $input) {
      services
      regions
      projectIds
      skus
      labelKeys
      dataSources {
        id
        name
        providerType
        isSyncing
      }
    }
  }
`;

const LIST_SAVED_FILTERS = gql`
  query ListSavedFilters($tenantId: ID!, $page: FilterPage!) {
    listSavedFilters(tenantId: $tenantId, page: $page) {
      id
      name
      page
      filters
    }
  }
`;

const GET_LABEL_VALUES = gql`
  query GetLabelValues($input: LabelValuesInput!) {
    getLabelValues(input: $input) {
      values
    }
  }
`;

const CREATE_SAVED_FILTER = gql`
  mutation CreateSavedFilter($input: CreateSavedFilterInput!) {
    createSavedFilter(input: $input) {
      id
      name
      page
      filters
    }
  }
`;

const EDIT_SAVED_FILTER_NAME = gql`
  mutation EditSavedFilterName($id: ID!, $newName: String!) {
    editSavedFilterName(id: $id, newName: $newName) {
      id
      name
      page
    }
  }
`;

const UPDATE_SAVED_FILTER = gql`
  mutation UpdateSavedFilter($input: UpdateSavedFilterInput!) {
    updateSavedFilter(input: $input) {
      id
      filters
      page
    }
  }
`;

const DELETE_SAVED_FILTER = gql`
  mutation DeleteSavedFilter($id: ID!) {
    deleteSavedFilter(id: $id) {
      id
    }
  }
`;

// DELETE THIS from your main file
export interface CustomPickerDayProps extends PickersDayProps {
  dayIsBetween?: boolean;
  isFirstDay?: boolean;
  isLastDay?: boolean;
}


// interface CustomPickerDayProps extends PickersDayProps {
//   dayIsBetween?: boolean;
//   isFirstDay?: boolean;
//   isLastDay?: boolean;
// }

// const CustomPickersDay = styled(PickersDay, {
//   shouldForwardProp: (prop) => prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay',
// })<CustomPickerDayProps>(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
//   ...(dayIsBetween && {
//     borderRadius: 0,
//     backgroundColor: alpha("#0064FF", 0.1),
//     color: theme.palette.text.primary,
//     '&:hover, &:focus': {
//       backgroundColor: alpha("#0064FF", 0.2),
//     },
//   }),
//   ...(isFirstDay && {
//     borderTopLeftRadius: '50%',
//     borderBottomLeftRadius: '50%',
//   }),
//   ...(isLastDay && {
//     borderTopRightRadius: '50%',
//     borderBottomRightRadius: '50%',
//   }),
//   '&.Mui-selected': {
//     backgroundColor: "#0064FF",
//     color: theme.palette.common.white,
//     '&:hover, &:focus': {
//         backgroundColor: "#0052cc",
//     }
//   },
// }));


const UserFriendlyError = ({ message }: { message: string }) => (
    <Paper
        elevation={0}
        sx={{
            p: 3,
            mt: -2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            height:'550px',
            mb:30
        }}
    >
        <img
            src="/icons/no-data.svg"
            alt="Warning"
            style={{ width: 100, height: 100, marginBottom: '16px' }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Oops! Something went wrong.
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            {message}
        </Typography>

        <Box>
            <Button
                onClick={() => window.location.reload()}
                sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    color: '#0064FF'
                }}
            >
                Refresh the Page
            </Button>
        </Box>
    </Paper>
);

const getFriendlyErrorMessage = (error: any): string => {
    if (error.message.includes('Failed to fetch')) {
        return "We couldn't reach our servers. Please check your internet connection and try again.";
    }
    return "An unexpected issue occurred while trying to load your data. Please try refreshing the page.";
};

const CustomDateRangePicker = ({ value, onChange }: { value: { startDate: Dayjs | null, endDate: Dayjs | null }, onChange: (newValue: { startDate: Dayjs | null, endDate: Dayjs | null }) => void }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [tempStartDate, setTempStartDate] = React.useState(value.startDate);
    const [tempEndDate, setTempEndDate] = React.useState(value.endDate);

    React.useEffect(() => {
        setTempStartDate(value.startDate);
        setTempEndDate(value.endDate);
    }, [value.startDate, value.endDate]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempStartDate(value.startDate);
        setTempEndDate(value.endDate);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'date-range-popper' : undefined;

    const handleTempDateChange = (newDate: Dayjs | null) => {
        if (!newDate) return;
        if (!tempStartDate) {
            setTempStartDate(newDate);
            setTempEndDate(null);
        } else if (tempStartDate && !tempEndDate) {
            if (newDate.isBefore(tempStartDate)) {
                setTempStartDate(newDate);
            } else {
                setTempEndDate(newDate);
            }
        } else if (tempStartDate && tempEndDate) {
            setTempStartDate(newDate);
            setTempEndDate(null);
        }
    };

    const handleOk = () => {
        onChange({ startDate: tempStartDate, endDate: tempEndDate });
        handleClose();
    };

    const handleCancel = () => {
        handleClose();
    };

    // --- NEW REUSABLE COMPONENT ---

    const renderDay = useCallback((props: PickersDayProps) => {
        const { day } = props;
        const start = tempStartDate;
        const end = tempEndDate;

        if (!start) {
            return <PickersDay {...props} />;
        }

        const dayIsBetween = end ? day.isAfter(start, 'day') && day.isBefore(end, 'day') : false;
        const isFirstDay = day.isSame(start, 'day');
        const isLastDay = day.isSame(end, 'day');

        return (
            <CustomPickersDay
                {...props}
                disableMargin
                dayIsBetween={dayIsBetween}
                isFirstDay={isFirstDay}
                isLastDay={isLastDay}
            />
        );
    }, [tempStartDate, tempEndDate]);

    // const renderDay: React.FC<PickersDayProps> = (props) => {
    //     const { day } = props;
    //     const start = tempStartDate;
    //     const end = tempEndDate;

    //     if (!start) {
    //         return <PickersDay {...props} />;
    //     }

    //     const dayIsBetween = end ? day.isAfter(start, 'day') && day.isBefore(end, 'day') : false;
    //     const isFirstDay = day.isSame(start, 'day');
    //     const isLastDay = day.isSame(end, 'day');

    //     return (
    //         <CustomPickersDay
    //             {...props}
    //             disableMargin
    //             dayIsBetween={dayIsBetween}
    //             isFirstDay={isFirstDay}
    //             isLastDay={isLastDay}
    //         />
    //     );
    // };

    const formattedValue = React.useMemo(() => {
        const { startDate, endDate } = value;
        if (startDate && endDate) {
            return `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;
        }
        if (startDate) {
            return `${startDate.format('DD/MM/YYYY')}`;
        }
        return '';
    }, [value]);

    return (
        <>
            <TextField
                aria-describedby={id}
                fullWidth
                size="small"
                value={formattedValue}
                onClick={handleClick}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={handleClick} edge="end">
                                <img
                                    src="/icons/calendar-icon.svg"
                                    alt="Calendar"
                                    style={{ width: 15, height: 15 }}
                                />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: { 
                        borderRadius: '8px', 
                        fontSize: '12px',      // Changed from 14px
                        fontFamily: 'Inter'    // Added font family
                    }
                    // sx: { borderRadius: '8px', fontSize: '14px' }
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#0064FF',
                        },
                    },
                }}
            />
            <Popper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
                <Paper elevation={3} sx={{borderRadius: '12px', overflow: 'hidden'}}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <StaticDatePicker
                            displayStaticWrapperAs="desktop"
                            value={tempEndDate || tempStartDate}
                            onChange={handleTempDateChange}
                            slots={{ day: renderDay, actionBar: () => null }}
                            dayOfWeekFormatter={(day) => day.format('dd')}
                            // dayOfWeekFormatter={(day) => day.format('dd').charAt(0).toUpperCase()}

                            slotProps={{
                                calendarHeader: { sx: { '.MuiPickersCalendarHeader-labelContainer': { fontSize: '16px', fontWeight: 500 } } },
                                        layout: { 
                                        sx: { 
                                            // --- ✅ THIS IS THE FIX ---
                                            // The container's only job is to hide the overflow from our grid trick.
                                            '& div[role="grid"]': {
                                                overflow: 'hidden',
                                            },
                                            // -------------------------
                                            '.MuiPickersLayout-contentWrapper': { width: '100%' }, 
                                            minWidth: 'auto',
                                        } 
                                    }
                                // layout: { sx: { '.MuiPickersLayout-contentWrapper': { width: '100%', }, minWidth: 'auto', } }
                            }}
                        />
                    </LocalizationProvider>
                    <Divider />
                    <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={handleCancel} sx={{textTransform: 'none', color: 'text.secondary'}}>Cancel</Button>
                        <Button onClick={handleOk} variant="contained" disableElevation sx={{textTransform: 'none', borderRadius: '6px', backgroundColor: '#0064FF', '&:hover': {backgroundColor: '#0052cc'} }}>OK</Button>
                    </Box>
                </Paper>
            </Popper>
        </>
    );
};

const CustomArrowIcon = (props: any & { open?: boolean }) => (
    <svg
        {...props}
        width="12"
        height="12"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
            ...props.style,
            transform: props.open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
        }}
    >
        <path d="M3.33331 5.66669L7.99998 10.3334L12.6666 5.66669" stroke="#333333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const NoSavedFiltersIcon = () => (
    <img
        src={`/icons/no-saved-filters.svg`}
        alt="No saved filters illustration"
        style={{ width: '50px', height: '50px'}}
    />
);

export interface SavedFilter {
    id: string;
    name: string;
    page: string;
    filters: any;
}

export interface FilterBarProps {
    onFiltersChange: (filters: any) => void;
    tenantId: string; // This can still be used for display logic if needed
    initialTenantId: string; // This is the one we'll use for logic
    onDataSourceInfoChange: (dataSources: DataSourceOption[]) => void;
}

// --- NEW REUSABLE COMPONENT ---
const SyncingBadge = () => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '20px',
            px: 1,
            ml: 0.5, // Add a little space before the badge
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 193, 7, 0.2)',
            color: 'rgba(180, 120, 0)',
            fontSize: '12px',
            fontWeight: 500,
        }}
    >
        Syncing
    </Box>
);

const FilterBar: React.FC<FilterBarProps> = ({ onFiltersChange, tenantId, initialTenantId, onDataSourceInfoChange }) => {
    // --- STATE FOR INITIALIZATION CONTROL ---
    const [isInitialized, setIsInitialized] = useState(false);

    // Get preloaded options from context.
    const { filterOptions: preloadedFilterOptions } = useCostAnalysisContext();

    // The FilterBar KEEPS its own internal state.
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        services: [], regions: [], projectIds: [], skus: [], labelKeys: [], dataSources: []
    });

    // All other FilterBar state definitions
    const [savedFiltersAnchorEl, setSavedFiltersAnchorEl] = useState<null | HTMLElement>(null);
    const [groupByAnchorEl, setGroupByAnchorEl] = useState<null | HTMLElement>(null);
    const [timeRangeAnchorEl, setTimeRangeAnchorEl] = useState<null | HTMLElement>(null);
    const [dataSourceAnchorEl, setDataSourceAnchorEl] = useState<null | HTMLElement>(null);
    const [servicesAnchorEl, setServicesAnchorEl] = useState<null | HTMLElement>(null);
    const [addFilterAnchorEl, setAddFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [labelFilterAnchorEl, setLabelFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [labelFilters, setLabelFilters] = useState<LabelFilterPair[]>([]);
    const [loadingLabelKey, setLoadingLabelKey] = useState<string | null>(null);

    const isSingleSourceSyncing = useMemo(() => {
        const allSources = filterOptions.dataSources || [];
        return allSources.length === 1 && allSources[0].isSyncing;
    }, [filterOptions.dataSources]);

    const [labelValueOptions, setLabelValueOptions] = useState<{ [key: string]: string[] }>({});
    const [activeLabelKey, setActiveLabelKey] = useState<string | null>(null);
    const [groupBy, setGroupBy] = useState('Services');
    const [tempGroupBy, setTempGroupBy] = useState(groupBy);
    const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);
    const [selectedServices, setSelectedServices] = useState<string[]>([]);
    const [tempSelectedDataSources, setTempSelectedDataSources] = useState<string[]>([]);
    const [dataSourceSearchTerm, setDataSourceSearchTerm] = useState('');
    const [tempSelectedServices, setTempSelectedServices] = useState<string[]>([]);
    const [serviceSearchTerm, setServiceSearchTerm] = useState('');
    const [activeDynamicFilters, setActiveDynamicFilters] = useState<string[]>([]);
    const [dynamicFilterState, setDynamicFilterState] = useState<{ [key: string]: any }>({});
    const [dynamicFilterAnchorEl, setDynamicFilterAnchorEl] = useState<HTMLElement | null>(null);
    const [activeDynamicFilterMenu, setActiveDynamicFilterMenu] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState('Last 30 days');
    const [timeRangeSelection, setTimeRangeSelection] = useState('Last 30 days');
    const [customStartDate, setCustomStartDate] = useState<Dayjs | null>(null);
    const [customEndDate, setCustomEndDate] = useState<Dayjs | null>(null);
    const [tempTimeRangeSelection, setTempTimeRangeSelection] = useState(timeRangeSelection);
    const [tempCustomStartDate, setTempCustomStartDate] = useState<Dayjs | null>(customStartDate);
    const [tempCustomEndDate, setTempCustomEndDate] = useState<Dayjs | null>(customEndDate);
    const [isSaveFilterDialogOpen, setIsSaveFilterDialogOpen] = useState(false);
    const [activeSavedFilterId, setActiveSavedFilterId] = useState<string | null>(null);
    const [dialogMode, setDialogMode] = useState<'save' | 'edit' | 'saveOrUpdate'>('save');
    const [filterToEdit, setFilterToEdit] = useState<SavedFilter | null>(null);
    const [originalAppliedFilterState, setOriginalAppliedFilterState] = useState<any>(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // --- EFFECT 1: Populate state from context ---
    useEffect(() => {
        if (preloadedFilterOptions) {
            setFilterOptions(preloadedFilterOptions);
            onDataSourceInfoChange(preloadedFilterOptions.dataSources || []);
        }
    }, [preloadedFilterOptions, onDataSourceInfoChange]);

    // --- All GraphQL Hooks (useQuery, useLazyQuery, useMutation) ---
    const { data: savedFiltersData, refetch: refetchSavedFilters } = useQuery(LIST_SAVED_FILTERS, {
        variables: { tenantId, page: 'COST_ANALYSIS' },
    });
    const savedFilters: SavedFilter[] = savedFiltersData?.listSavedFilters || [];

    const [getLabelValues] = useLazyQuery(GET_LABEL_VALUES, {
      onCompleted: (data) => {
        if (data?.getLabelValues && activeLabelKey) {
          setLabelValueOptions(prev => ({ ...prev, [activeLabelKey]: data.getLabelValues.values }));
        }
        setLoadingLabelKey(null);
      },
      onError: (error) => {
        console.error("Error fetching label values:", error);
        setLoadingLabelKey(null);
      }
    });

    const [createFilter] = useMutation(CREATE_SAVED_FILTER, { onCompleted: () => refetchSavedFilters() });
    const [editFilterName] = useMutation(EDIT_SAVED_FILTER_NAME);
    const [updateFilter] = useMutation(UPDATE_SAVED_FILTER);
    const [deleteFilter] = useMutation(DELETE_SAVED_FILTER, { onCompleted: () => refetchSavedFilters() });

    // --- EFFECT 2: The critical "Load from LocalStorage or Apply Defaults" logic ---
    useEffect(() => {
        if (isInitialized || !initialTenantId || filterOptions.dataSources.length === 0) {
            return;
        }
        const LOCAL_STORAGE_KEY = `costAnalysisFilters_${initialTenantId}`;
        const savedFiltersJSON = localStorage.getItem(LOCAL_STORAGE_KEY);
        let successfullyLoaded = false;
        const validDataSourceMap = new Map(filterOptions.dataSources.map(ds => [ds.id, ds]));

        if (savedFiltersJSON) {
            try {
                const savedState = JSON.parse(savedFiltersJSON);
                console.log("✅ Found saved state in localStorage. Loading and reconciling...", savedState);

                const storedDataSources = savedState.selectedDataSources || [];
                const reconciledDataSources = storedDataSources.filter((id: string) => {
                    const dataSource = validDataSourceMap.get(id);
                    return dataSource && !dataSource.isSyncing;
                });
                setSelectedDataSources(reconciledDataSources);

                setGroupBy(savedState.groupBy || 'Services');
                setSelectedServices(savedState.selectedServices || []);
                setLabelFilters(savedState.labelFilters || []);
                setActiveDynamicFilters(savedState.activeDynamicFilters || []);
                setTimeRange(savedState.timeRange || 'Last 30 days');
                setTimeRangeSelection(savedState.timeRangeSelection || 'Last 30 days');
                setCustomStartDate(savedState.customStartDate ? dayjs(savedState.customStartDate) : null);
                setCustomEndDate(savedState.customEndDate ? dayjs(savedState.customEndDate) : null);
                setActiveSavedFilterId(savedState.activeSavedFilterId || null);
                setDynamicFilterState(savedState.dynamicFilterState || {});

                successfullyLoaded = true;
            } catch (error) {
                console.error("❌ Failed to parse filters from localStorage, applying defaults.");
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        }

        if (!successfullyLoaded) {
            console.log("ℹ️ No saved state found, applying application defaults (excluding syncing sources).");

            const defaultSelectableSources = filterOptions.dataSources
                .filter(ds => !ds.isSyncing)
                .map(ds => ds.id);
            setSelectedDataSources(defaultSelectableSources);

            setGroupBy('Services');
            setTimeRange('Last 30 days');
            setTimeRangeSelection('Last 30 days');
            setCustomStartDate(dayjs().subtract(29, 'day'));
            setCustomEndDate(dayjs());
            setSelectedServices([]);
            setLabelFilters([]);
            setActiveDynamicFilters([]);
            setDynamicFilterState({});
        }

        setIsInitialized(true);

    }, [filterOptions.dataSources, initialTenantId, isInitialized]);

    // --- EFFECT 3: The "Save to LocalStorage" logic ---
    useEffect(() => {
        if (!isInitialized) return;
        // Use the STABLE initialTenantId for saving
        const LOCAL_STORAGE_KEY = `costAnalysisFilters_${initialTenantId}`;
        const filtersToSave = {
            groupBy, timeRange, timeRangeSelection,
            customStartDate: customStartDate ? customStartDate.toISOString() : null,
            customEndDate: customEndDate ? customEndDate.toISOString() : null,
            selectedDataSources, selectedServices, labelFilters, activeDynamicFilters,
            activeSavedFilterId, dynamicFilterState,
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtersToSave));
    }, [
        groupBy, timeRange, timeRangeSelection, customStartDate, customEndDate,
        selectedDataSources, selectedServices, labelFilters, activeDynamicFilters,
        dynamicFilterState, tenantId, activeSavedFilterId, initialTenantId, isInitialized
    ]);

    // --- EFFECT 4: The "onFiltersChange" callback to the parent ---
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        const appliedFilters = {
            groupBy, timeRange, timeRangeSelection, customStartDate, customEndDate,
            selectedDataSources, selectedServices, labelFilters, dynamicFilterState,
        };
        onFiltersChange(appliedFilters);
    }, [
        groupBy, timeRange, timeRangeSelection, customStartDate, customEndDate,
        selectedDataSources, selectedServices, labelFilters, dynamicFilterState,
        onFiltersChange, isInitialized
    ]);

    // --- All memoized values and handler functions remain the same ---
    const savedFiltersMenuOpen = Boolean(savedFiltersAnchorEl);
    const groupByMenuOpen = Boolean(groupByAnchorEl);
    const timeRangeMenuOpen = Boolean(timeRangeAnchorEl);
    const dataSourceMenuOpen = Boolean(dataSourceAnchorEl);
    const servicesMenuOpen = Boolean(servicesAnchorEl);
    const addFilterMenuOpen = Boolean(addFilterAnchorEl);
    const isLabelFilterOpen = Boolean(labelFilterAnchorEl);

    const dataSourcesByGroup = useMemo(() => {
        return (filterOptions.dataSources || []).reduce((acc: { [key: string]: DataSourceOption[] }, source: DataSourceOption) => {
            const provider = source.providerType;
            if (!acc[provider]) {
                acc[provider] = [];
            }
            acc[provider].push(source);
            return acc;
        }, {});
    }, [filterOptions.dataSources]);

    const allDataSources: DataSourceOption[] = useMemo(() => Object.values(dataSourcesByGroup).flat(), [dataSourcesByGroup]);
    const groupByOptions = ['Data Sources', 'Services', 'SKU', 'Region', 'Project'];
    const timeRangeOptions = ['Today', 'Last 7 days', 'Last 15 days', 'Last 30 days', 'Custom'];
    const allServices = useMemo(() => filterOptions.services || [], [filterOptions.services]);

    const moreFiltersOptions = useMemo(() => {
      return ['SKU', 'Region', 'Project'];
    }, []);

    const dynamicFilterOptions: {[key: string]: string[]} = useMemo(() => ({
        'SKU': filterOptions.skus || [],
        'Region': filterOptions.regions || [],
        'Project': filterOptions.projectIds || [],
        ...labelValueOptions
    }), [filterOptions.skus, filterOptions.regions, filterOptions.projectIds, labelValueOptions]);

    const availableMoreFilters = moreFiltersOptions.filter(f => !activeDynamicFilters.includes(f));

    // const dataSourceButtonText = React.useMemo(() => {
    //     if (selectedDataSources.length === 1 && selectedDataSources[0] === 'ALL_SOURCES') {
    //         return "All";
    //     }

    //     const gcpSources: DataSourceOption[] = dataSourcesByGroup['GCP'] || [];
    //     const awsSources: DataSourceOption[] = dataSourcesByGroup['AWS'] || [];

    //     const selectedCount = selectedDataSources.length;
    //     const totalPossibleSources = allDataSources.length;
    //     const selectedGcpCount = selectedDataSources.filter(id => gcpSources.some(s => s.id === id)).length;
    //     const selectedAwsCount = selectedDataSources.filter(id => awsSources.some(s => s.id === id)).length;

    //     if (selectedCount === totalPossibleSources && totalPossibleSources > 0) return "All";
    //     if (selectedGcpCount === gcpSources.length && gcpSources.length > 0 && selectedAwsCount === 0) return "GCP All";
    //     if (selectedAwsCount === awsSources.length && awsSources.length > 0 && selectedGcpCount === 0) return "AWS All";

    //     if (selectedCount > 0) {
    //         if (selectedGcpCount > 0 && selectedAwsCount === 0) return `GCP ${selectedGcpCount} selected`;
    //         if (selectedAwsCount > 0 && selectedGcpCount === 0) return `AWS ${selectedAwsCount} selected`;
    //         return `${selectedCount} selected`;
    //     }
    //     return "Data Source";
    // }, [selectedDataSources, dataSourcesByGroup, allDataSources]);

    const dataSourceButtonText = React.useMemo(() => {
        const allSources = filterOptions.dataSources || [];
        // Handle the single syncing data source case first
        if (allSources.length === 1 && allSources[0].isSyncing) {
            const provider = allSources[0].providerType; // e.g., "GCP", "AWS"
            
            // Return JSX instead of a string
            return (
                <>
                  {provider}
                  <SyncingBadge />
                </>
            );
        }

        // --- Existing logic for all other cases (this remains the same) ---
        if (selectedDataSources.length === 1 && selectedDataSources[0] === 'ALL_SOURCES') {
            return "All";
        }

        const gcpSources: DataSourceOption[] = dataSourcesByGroup['GCP'] || [];
        const awsSources: DataSourceOption[] = dataSourcesByGroup['AWS'] || [];

        const selectedCount = selectedDataSources.length;
        const totalPossibleSources = allDataSources.length;
        const selectedGcpCount = selectedDataSources.filter(id => gcpSources.some(s => s.id === id)).length;
        const selectedAwsCount = selectedDataSources.filter(id => awsSources.some(s => s.id === id)).length;

        if (selectedCount === totalPossibleSources && totalPossibleSources > 0) return "All";
        if (selectedGcpCount === gcpSources.length && gcpSources.length > 0 && selectedAwsCount === 0) return "GCP All";
        if (selectedAwsCount === awsSources.length && awsSources.length > 0 && selectedGcpCount === 0) return "AWS All";

        if (selectedCount > 0) {
            if (selectedGcpCount > 0 && selectedAwsCount === 0) return `GCP ${selectedGcpCount} selected`;
            if (selectedAwsCount > 0 && selectedGcpCount === 0) return `AWS ${selectedAwsCount} selected`;
            return `${selectedCount} selected`;
        }
        
        return "Data Source";
    }, [selectedDataSources, dataSourcesByGroup, allDataSources, filterOptions.dataSources]);

    const filteredServices = allServices.filter((service: string) =>
        service.toLowerCase().includes(serviceSearchTerm.toLowerCase())
    );

    const handleSavedFiltersClick = (event: React.MouseEvent<HTMLElement>) => setSavedFiltersAnchorEl(event.currentTarget);
    const handleSavedFiltersClose = () => setSavedFiltersAnchorEl(null);

    const handleGroupByClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempGroupBy(groupBy);
        setGroupByAnchorEl(event.currentTarget);
    };
    const handleGroupByClose = () => setGroupByAnchorEl(null);
    const handleGroupByApply = () => {
        setGroupBy(tempGroupBy);
        handleGroupByClose();
    };

    const handleTimeRangeClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempTimeRangeSelection(timeRangeSelection || 'Last 30 days');
        setTempCustomStartDate(customStartDate);
        setTempCustomEndDate(customEndDate);
        setTimeRangeAnchorEl(event.currentTarget);
    };
    const handleTimeRangeClose = () => setTimeRangeAnchorEl(null);

    const handleLabelFilterClick = (event: React.MouseEvent<HTMLElement>) => {
      setLabelFilterAnchorEl(event.currentTarget);
    };

    const handleFetchLabelValues = (key: string) => {
      if (key && !labelValueOptions[key]) {
        setActiveLabelKey(key);
        setLoadingLabelKey(key); // Start loading indicator
        getLabelValues({
          variables: {
            input: {
              tenantId,
              labelKey: key,
              dateRange: {
                start: (customStartDate || dayjs().subtract(29, 'day')).format('YYYY-MM-DD'),
                end: (customEndDate || dayjs()).format('YYYY-MM-DD')
              }
            }
          }
        });
      }
    };

    const handleTimeRangeApply = () => {
        setTimeRangeSelection(tempTimeRangeSelection);
        let newStartDate: Dayjs | null = null;
        let newEndDate: Dayjs | null = dayjs();

        switch (tempTimeRangeSelection) {
            case 'Today': newStartDate = dayjs(); break;
            case 'Last 7 days': newStartDate = dayjs().subtract(6, 'day'); break;
            case 'Last 15 days': newStartDate = dayjs().subtract(14, 'day'); break;
            case 'Last 30 days': newStartDate = dayjs().subtract(29, 'day'); break;
            case 'Custom':
                newStartDate = tempCustomStartDate;
                newEndDate = tempCustomEndDate;
                break;
            default: newStartDate = dayjs().subtract(29, 'day'); break;
        }

        setCustomStartDate(newStartDate);
        setCustomEndDate(newEndDate);

        if (tempTimeRangeSelection === 'Custom' && newStartDate && newEndDate) {
            setTimeRange(`${newStartDate.format('DD/MM/YYYY')} - ${newEndDate.format('DD/MM/YYYY')}`);
        } else {
            setTimeRange(tempTimeRangeSelection);
        }
        handleTimeRangeClose();
    };

    const getActiveGroupFromSources = (sourceIds: string[]) => {
        if (sourceIds.length === 0) return null;
        const firstId = sourceIds[0];
        const gcpSources: DataSourceOption[] = dataSourcesByGroup['GCP'] || [];
        if (gcpSources.some(s => s.id === firstId)) return 'GCP';
        const awsSources: DataSourceOption[] = dataSourcesByGroup['AWS'] || [];
        if (awsSources.some(s => s.id === firstId)) return 'AWS';
        return null;
    };

    const handleDataSourceMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setTempSelectedDataSources([...selectedDataSources]);
        setDataSourceSearchTerm('');
        setDataSourceAnchorEl(event.currentTarget);
    };
    const handleDataSourceMenuClose = () => setDataSourceAnchorEl(null);
    const handleDataSourceSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setDataSourceSearchTerm(event.target.value);

    const handleToggleDataSource = (sourceId: string) => {
        const sourceGroupEntry = Object.entries(dataSourcesByGroup).find(([, sources]) => sources.some(s => s.id === sourceId));
        const sourceGroup = sourceGroupEntry ? sourceGroupEntry[0] : undefined;
        setTempSelectedDataSources(prev => {
            const activeGroup = getActiveGroupFromSources(prev);
            if (activeGroup && sourceGroup !== activeGroup) {
                return [sourceId];
            }
            return prev.includes(sourceId) ? prev.filter(s => s !== sourceId) : [...prev, sourceId];
        });
    };
    const handleToggleDataSourceGroup = (groupName: string, groupSources: DataSourceOption[]) => {
        const groupSourceIds = groupSources.map(s => s.id);
        setTempSelectedDataSources(prev => {
            const areAllSelected = groupSourceIds.every(id => prev.includes(id)) && groupSourceIds.length === prev.length;
            return areAllSelected ? [] : groupSourceIds;
        });
    };
    const handleClearDataSources = () => setTempSelectedDataSources([]);
    const handleApplyDataSources = () => {
        setSelectedDataSources(tempSelectedDataSources);
        handleDataSourceMenuClose();
    };
    const activeDataSourceGroup = getActiveGroupFromSources(tempSelectedDataSources);

    const handleServicesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setTempSelectedServices([...selectedServices]);
        setServiceSearchTerm('');
        setServicesAnchorEl(event.currentTarget);
    };
    const handleServicesMenuClose = () => setServicesAnchorEl(null);
    const handleServiceSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setServiceSearchTerm(event.target.value);
    const handleToggleService = (service: string) => {
        setTempSelectedServices((prev) => prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]);
    };
    const handleToggleAllServices = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTempSelectedServices(event.target.checked ? allServices : []);
    };
    const handleClearServices = () => setTempSelectedServices([]);
    const handleApplyServices = () => {
        setSelectedServices(tempSelectedServices);
        handleServicesMenuClose();
    };

    const handleClearAllFilters = useCallback(() => {
        const nonSyncingDataSources = (filterOptions.dataSources || [])
            .filter(ds => !ds.isSyncing)
            .map(ds => ds.id);

        setGroupBy('Services');
        setTimeRange('Last 30 days');
        setTimeRangeSelection('Last 30 days');
        setCustomStartDate(dayjs().subtract(29, 'day'));
        setCustomEndDate(dayjs());
        setSelectedDataSources(nonSyncingDataSources);
        setSelectedServices([]);
        setLabelFilters([]);
        setActiveDynamicFilters([]);
        setDynamicFilterState({});
        setTempGroupBy('Services');
        setTempSelectedDataSources([]);
        setDataSourceSearchTerm('');
        setTempSelectedServices([]);
        setServiceSearchTerm('');
        setTempTimeRangeSelection('Last 30 days');
        setTempCustomStartDate(dayjs().subtract(29, 'day'));
        setTempCustomEndDate(dayjs());
        setOriginalAppliedFilterState(null);
        setActiveSavedFilterId(null);
    }, [filterOptions.dataSources]);

    const handleAddFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setAddFilterAnchorEl(event.currentTarget);
    };
    const handleAddFilterClose = () => {
        setAddFilterAnchorEl(null);
    };

    const handleAddFilter = (filterName: string) => {
        if (!activeDynamicFilters.includes(filterName)) {
            setActiveDynamicFilters(prev => [...prev, filterName]);
            setDynamicFilterState(prev => ({
                ...prev,
                [filterName]: {
                    selected: [],
                    tempSelected: [],
                    searchTerm: '',
                }
            }));
        }
        setAddFilterAnchorEl(null);
    };

    const handleDynamicMenuOpen = (event: React.MouseEvent<HTMLElement>, filterName: string) => {
        setDynamicFilterAnchorEl(event.currentTarget);
        setActiveDynamicFilterMenu(filterName);
        setDynamicFilterState(prev => ({
            ...prev,
            [filterName]: {
                ...prev[filterName],
                tempSelected: [...(prev[filterName]?.selected || [])],
            }
        }));
    };

    const handleDynamicMenuClose = () => {
        setDynamicFilterAnchorEl(null);
        setActiveDynamicFilterMenu(null);
    };

    const handleApplyDynamicFilter = (filterName: string) => {
        setDynamicFilterState(prev => ({
            ...prev,
            [filterName]: {
                ...prev[filterName],
                selected: [...(prev[filterName]?.tempSelected || [])],
            }
        }));
        handleDynamicMenuClose();
    };

    const handleDynamicFilterSearchChange = (event: React.ChangeEvent<HTMLInputElement>, filterName: string) => {
        setDynamicFilterState(prev => ({
            ...prev,
            [filterName]: {
                ...prev[filterName],
                searchTerm: event.target.value,
            }
        }));
    };

    const handleToggleDynamicFilterItem = (item: string, filterName: string) => {
        setDynamicFilterState(prev => {
            const currentTemp = prev[filterName].tempSelected;
            const newTemp = currentTemp.includes(item)
                ? currentTemp.filter((i: string) => i !== item)
                : [...currentTemp, item];
            return {
                ...prev,
                [filterName]: { ...prev[filterName], tempSelected: newTemp }
            }
        });
    };

    const handleClearDynamicFilter = (filterName: string) => {
        setDynamicFilterState(prev => ({
            ...prev,
            [filterName]: { ...prev[filterName], tempSelected: [] }
        }));
    };

    const getCurrentFilterState = useCallback(() => {
        return {
            groupBy,
            timeRange,
            timeRangeSelection,
            customStartDate: customStartDate ? customStartDate.toISOString() : null,
            customEndDate: customEndDate ? customEndDate.toISOString() : null,
            selectedDataSources,
            selectedServices,
            labelFilters,
            activeDynamicFilters,
            dynamicFilterState: Object.entries(dynamicFilterState).reduce((acc, [key, value]) => {
                acc[key] = { selected: value.selected };
                return acc;
            }, {} as {[key: string]: {selected: string[]}}),
        };
    }, [groupBy, timeRange, timeRangeSelection, customStartDate, customEndDate, selectedDataSources, selectedServices, labelFilters, activeDynamicFilters, dynamicFilterState]);

    const handleFilterSubmit = useCallback((nameOrAction: string, id?: string, state?: any) => {
        if (nameOrAction === 'UPDATE_EXISTING' && id) {
            const updatedState = getCurrentFilterState();
            updateFilter({ variables: { input: { id, filters: updatedState } } });
            setOriginalAppliedFilterState(updatedState);
            setToastMessage(`Filter updated successfully!`);
        } else if (id && nameOrAction !== 'UPDATE_EXISTING') {
            editFilterName({ variables: { id, newName: nameOrAction } });
            setToastMessage(`Filter "${nameOrAction}" updated successfully!`);
        } else {
            createFilter({ variables: { input: { name: nameOrAction, tenantId, filters: state, page: 'COST_ANALYSIS' } } })
              .then(response => {
                  const newFilter = response.data?.createSavedFilter;
                  if (newFilter) {
                      setActiveSavedFilterId(newFilter.id);
                  }
              });
            setToastMessage(`Filter "${nameOrAction}" saved successfully!`);
        }
        setToastOpen(true);
        setFilterToEdit(null);
    }, [getCurrentFilterState, tenantId, createFilter, editFilterName, updateFilter]);

    const handleSaveFilterClick = () => {
        if (activeSavedFilterId && isFilterStateChanged()) {
            setDialogMode('saveOrUpdate');
            const filterBeingEdited = savedFilters.find(f => f.id === activeSavedFilterId);
            setFilterToEdit(filterBeingEdited || null);
        } else {
            setDialogMode('save');
            setFilterToEdit(null);
        }
        setIsSaveFilterDialogOpen(true);
    };

    const handleApplySavedFilter = useCallback((selectedFilter: SavedFilter) => {
        const filterState = selectedFilter.filters;
        setOriginalAppliedFilterState(selectedFilter.filters);

        setGroupBy(filterState.groupBy || 'Services');
        setTimeRange(filterState.timeRange || '');
        setTimeRangeSelection(filterState.timeRangeSelection || 'Last 30 days');
        setCustomStartDate(filterState.customStartDate ? dayjs(filterState.customStartDate) : null);
        setCustomEndDate(filterState.customEndDate ? dayjs(filterState.customEndDate) : null);
        setSelectedDataSources(filterState.selectedDataSources || []);
        setSelectedServices(filterState.selectedServices || []);
        setLabelFilters(filterState.labelFilters || []);

        setActiveDynamicFilters(filterState.activeDynamicFilters || []);
        const newDynamicFilterState: {[key: string]: any} = {};
        if (filterState.dynamicFilterState) {
            for (const key in filterState.dynamicFilterState) {
                newDynamicFilterState[key] = {
                    selected: filterState.dynamicFilterState[key].selected || [],
                    tempSelected: [],
                    anchorEl: null,
                };
            }
        }
        setDynamicFilterState(newDynamicFilterState);
        setActiveSavedFilterId(selectedFilter.id);
        handleSavedFiltersClose();
    }, []);

    const handleEditSavedFilter = (event: React.MouseEvent, filterId: string) => {
        event.stopPropagation();
        const filterBeingEdited = savedFilters.find(f => f.id === filterId);
        if (filterBeingEdited) {
            setFilterToEdit(filterBeingEdited);
            setDialogMode('edit');
            setIsSaveFilterDialogOpen(true);
        }
    };

    const handleDeleteSavedFilter = (event: React.MouseEvent, filterId: string) => {
        event.stopPropagation();
        deleteFilter({ variables: { id: filterId } });
        if (activeSavedFilterId === filterId) {
            setActiveSavedFilterId(null);
        }
        setToastMessage("Filter deleted successfully!");
        setToastOpen(true);
    };

    const handleCloseToast = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setToastOpen(false);
    };

    const isDefaultState = useMemo(() => {
        const nonSyncingDataSources = (filterOptions.dataSources || []).filter(ds => !ds.isSyncing);
        const isDefaultGroupBy = groupBy === 'Services';
        const isDefaultTimeRange = timeRangeSelection === 'Last 30 days';
        const isDefaultDataSource = selectedDataSources.length === nonSyncingDataSources.length;
        const noOtherFilters = selectedServices.length === 0 &&
            labelFilters.length === 0 &&
            activeDynamicFilters.every(filterName => {
                const state = dynamicFilterState[filterName];
                return !state || !state.selected || state.selected.length === 0;
            }) &&
            activeSavedFilterId === null;

        return isDefaultGroupBy && isDefaultTimeRange && isDefaultDataSource && noOtherFilters;
    }, [groupBy, timeRangeSelection, selectedDataSources, selectedServices, labelFilters, activeDynamicFilters, dynamicFilterState, activeSavedFilterId, filterOptions.dataSources]);

    const isFilterStateChanged = useCallback(() => {
        if (!originalAppliedFilterState) {
            return false;
        }
        const currentStateString = JSON.stringify(getCurrentFilterState());
        const originalStateString = JSON.stringify(originalAppliedFilterState);
        return currentStateString !== originalStateString;
    }, [getCurrentFilterState, originalAppliedFilterState]);

    return (
        <Stack
            direction="row"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            flexGrow={3}
        // sx={{
        //     opacity: isInitialized ? 1 : 0,
        //     transform: isInitialized ? 'translateY(0)' : 'translateY(-10px)',
        //     transition: 'transform 1ms ease-out, opacity 200ms ease-out',
        // }}
        >
            <Button
                id="saved-filters-button"
                onClick={handleSavedFiltersClick}
                variant="outlined"
                endIcon={<CustomArrowIcon open={savedFiltersMenuOpen} />}
                sx={{
                    minWidth: 120,
                    height: '35px',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    borderRadius:'8px',
                    color: 'text.primary',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderColor: 'rgba(230, 230, 230, 1)',
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    ...(activeSavedFilterId && {
                        backgroundColor: 'rgba(243, 248, 254, 1)',
                        borderColor: 'transparent',
                        color: 'rgba(51, 51, 51, 1)',
                    }),
                    ...(savedFiltersMenuOpen && {
                        borderColor: '#0064FF',
                        backgroundColor: 'rgba(243, 248, 254, 1)',
                    }),
                    '&:hover': {
                        borderColor: '#0064FF',
                        backgroundColor: 'rgba(243, 248, 254, 1)',
                    }
                }}
            >
                {activeSavedFilterId
                    ? `Saved Filter: ${(savedFilters || []).find(f => f.id === activeSavedFilterId)?.name || 'No Filter'}`
                    : 'Saved Filter'
                }
            </Button>
            <Menu
                id="saved-filters-menu" anchorEl={savedFiltersAnchorEl} open={savedFiltersMenuOpen} onClose={handleSavedFiltersClose}
                PaperProps={{ sx: { width: '229px', borderRadius: '12px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)', mt: 1, maxHeight: 360, overflowY: 'auto' } }}
            >
                <Box sx={{ p: 1, marginLeft:1 ,mt:1}}>
                    <Typography sx={{ fontFamily: 'Inter',lineHeight:'20px', fontSize: '14px', fontWeight: 600, color: '#666666' }}> Select Saved Filters</Typography>
                </Box>
                <Divider />
                {savedFilters && savedFilters.length > 0 ? (
                    <Box sx={{ py: 1 }}>
                        {savedFilters.map((filter) => (
                            <MenuItem
                                key={filter.id}
                                onClick={() => handleApplySavedFilter(filter)}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    py: 1,
                                    px: 2,
                                    backgroundColor: activeSavedFilterId === filter.id ? 'rgba(243, 248, 254, 1)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: ' rgba(249, 250, 251, 1)',
                                    },
                                }}
                            >
                                <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'Inter', fontSize: '14px', fontWeight: 400, color: activeSavedFilterId === filter.id ? 'rgba(51, 51, 51, 1)' : 'rgba(102, 102, 102, 1)', }}>
                                    {filter.name}
                                </Typography>
                                <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleEditSavedFilter(e, filter.id)}
                                        sx={{
                                            color: 'rgba(153, 153, 153, 1)',
                                            '&:hover': {
                                                color: '#0064FF',
                                                backgroundColor: 'rgba(0, 100, 255, 0.1)',
                                            }
                                        }}
                                    >
                                    <img
                                        src="/icons/edit_icon.svg"
                                        alt="Edit Filter"
                                        style={{ width: 16, height: 16 }}
                                    />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleDeleteSavedFilter(e, filter.id)}
                                        sx={{
                                            color: 'rgba(153, 153, 153, 1)',
                                            '&:hover': {
                                                color: '#FF0000',
                                                backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                            }
                                        }}
                                    >
                                    <img
                                        src="/icons/delete_icon.svg"
                                        alt="Delete Filter"
                                        style={{ width: 16, height: 16 }}
                                    />
                                    </IconButton>
                                </Stack>
                            </MenuItem>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3, pt: 7, pb: 6 }}>
                        <NoSavedFiltersIcon />
                        <Typography variant="subtitle1" sx={{ fontSize: '14px', fontFamily: 'Inter', fontWeight: 600, mt: 1,color:'#333333' }}>No saved filters</Typography>
                        <Typography sx={{ fontFamily: 'Inter', mt: 0.5, fontSize: '12px', fontWeight: '400', px: 2, color: '#333333' }}>
                            Apply filters and save them for quick access later.
                        </Typography>
                    </Box>
                )}
            </Menu>

            <Button
                id="group-by-button"
                onClick={handleGroupByClick}
                variant="outlined"
                disableElevation
                endIcon={<CustomArrowIcon open={groupByMenuOpen} />}
                sx={{ textTransform: 'none', color: 'text.primary', px: 1.5, height: '35px', borderRadius: '8px', fontSize: '14px', fontWeight: 500, borderColor: groupByMenuOpen ? '#0064FF' : 'transparent', backgroundColor: 'rgba(243, 248, 254, 1)', '&:hover': { borderColor: '#0064FF', backgroundColor: 'rgba(220, 235, 253, 1)' } }}
            >
                <Box component="span" sx={{ fontSize: '14px', }}>Group By: </Box>
                <Box component="span" sx={{ fontWeight: 400 }}>{groupBy}</Box>
            </Button>
            <Menu
                id="group-by-menu" anchorEl={groupByAnchorEl} open={groupByMenuOpen} onClose={handleGroupByClose}
                PaperProps={{ elevation: 0, sx: { width: '229px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', mt: 1 } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box>
                    <Typography sx={{ p: '16px 24px 8px 14px', fontFamily: 'Inter',lineHeight:'20px', fontSize: '14px', fontWeight: 600, color: ' rgba(102, 102, 102, 1)' }}>Select</Typography>
                    <Divider />
                    <RadioGroup value={tempGroupBy} onChange={(e) => setTempGroupBy(e.target.value)} sx={{ py: '8px' }}>
                        {groupByOptions.map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio size="small" sx={{  '&.Mui-checked': { color: '#0064FF' } }} />}
                                label={option}
                                sx={{ m: 0, pl: '8px', pr: '24px', py: '4px', width: '100%', boxSizing: 'border-box', backgroundColor: tempGroupBy === option ? 'rgba(232, 240, 254, 1)' : 'transparent', '& .MuiTypography-root': { fontFamily: 'Inter', fontSize: '14px', color: tempGroupBy === option ? 'text.primary' : 'text.secondary', fontWeight: tempGroupBy === option ? 400 : 400 } }}
                            />
                        ))}
                    </RadioGroup>
                    <Divider />
                    <Box sx={{ height:'30px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleGroupByApply} sx={{ textTransform: 'none', fontFamily: 'Inter', fontWeight: 500, color: '#0064FF', fontSize: '14px',marginRight:'8px'}}>Apply</Button>
                    </Box>
                </Box>
            </Menu>
            <Divider orientation="vertical" sx={{ height: 30, alignSelf: 'center' }} />

            <Button
                id="time-range-button"
                onClick={handleTimeRangeClick}
                variant="outlined"
                endIcon={<CustomArrowIcon open={timeRangeMenuOpen} />}
                sx={{ height: '35px', textTransform: 'none', fontFamily: 'Inter', color: 'text.primary', fontSize: '14px', fontWeight: 500, borderRadius: '8px', minWidth: '220px', px: 1.5, backgroundColor: 'transparent', borderColor: 'rgba(230, 230, 230, 1)', ...(timeRange && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: 'transparent', }), '&:hover': { borderColor: '#0064FF', backgroundColor: 'rgba(243, 248, 254, 1)', }, ...(timeRangeMenuOpen && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: '#0064FF', }), }}
            >
                {timeRange ? ( <> <Box component="span" sx={{ color: 'rgba(51, 51, 51, 1)', fontWeight: 500 }}>Time Range: </Box> <Box component="span" sx={{ fontWeight: 400 }}>{timeRange}</Box> </> ) : ( 'Time Range' )}
            </Button>
            <Menu
                id="time-range-menu"
                anchorEl={timeRangeAnchorEl}
                open={timeRangeMenuOpen}
                onClose={handleTimeRangeClose}
                MenuListProps={{ disablePadding: true }}
                PaperProps={{
                    elevation: 0,
                    sx: { borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', mt: 1, }
                }}
            >
                <Box sx={{ width: '320px' }}>
                    <Typography sx={{ p: '20px 24px 8px 14px', fontFamily: 'Inter',lineHeight:'20px', fontSize: '14px', fontWeight: 600, color: ' #666666' }}>
                        Select Time Range
                    </Typography>
                    <Divider />
                    <RadioGroup
                        value={tempTimeRangeSelection}
                        onChange={(e) => setTempTimeRangeSelection(e.target.value)}
                        sx={{ py: 1 }}
                    >
                        {timeRangeOptions.map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#0064FF' } }} />}
                                label={option}
                                sx={{ m: 0, width: '100%', boxSizing: 'border-box', py: '4px', px: 1, borderRadius: '0px', backgroundColor: tempTimeRangeSelection === option ? '#F2F7FF' : 'transparent', '& .MuiTypography-root': { fontFamily: 'Inter', fontSize: '14px', color: tempTimeRangeSelection === option ? 'text.primary' : 'text.secondary' } }}
                            />
                        ))}
                    </RadioGroup>
                    {tempTimeRangeSelection === 'Custom' && (
                        <Box sx={{ px: 2, mt: 1, pb: 1 }}>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                                Select Date Range
                            </Typography>
                            <CustomDateRangePicker
                                value={{ startDate: tempCustomStartDate, endDate: tempCustomEndDate }}
                                onChange={(newValue) => {
                                    setTempCustomStartDate(newValue.startDate);
                                    setTempCustomEndDate(newValue.endDate);
                                }}
                            />
                        </Box>
                    )}
                </Box>
                <Divider />
                <Box sx={{ p: '4px 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center',height:'40px' }}>
                    <Button onClick={() => { setTimeRange(''); handleTimeRangeClose(); }} sx={{ textTransform: 'none', color: '#666666', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', marginRight: '-10px' }}>
                        Clear
                    </Button>
                    <Button onClick={handleTimeRangeApply} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', marginRight: '-8px' }}>
                        Apply
                    </Button>
                </Box>
            </Menu>

            <Button
                id="data-source-button"
                onClick={handleDataSourceMenuOpen}
                variant="outlined"
                endIcon={<CustomArrowIcon open={dataSourceMenuOpen} />}
                sx={{
                    height: '35px',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    color: 'text.primary',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    minWidth: '130px',
                    px: '12px',
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(230, 230, 230, 1)',
                    
                    // Style for when items are selected (but not syncing)
                    ...(selectedDataSources.length > 0 && { 
                        backgroundColor: 'rgba(243, 248, 254, 1)', 
                        borderColor: 'transparent', 
                    }),
                    
                    // --- NEW STYLE FOR THE SYNCING STATE ---
                    ...(isSingleSourceSyncing && {
                        backgroundColor: '#F2F7FF', // Your requested background color
                        borderColor: 'transparent',
                    }),
                    
                    // Style for when the menu is open (overrides others)
                    ...(dataSourceMenuOpen && { 
                        backgroundColor: 'rgba(243, 248, 254, 1)', 
                        borderColor: '#0064FF', 
                    }),
                    
                    '&:hover': { 
                        borderColor: '#0064FF', 
                        backgroundColor: 'rgba(243, 248, 254, 1)', 
                    },
                }}
            >

            {/* <Button
                id="data-source-button"
                onClick={handleDataSourceMenuOpen}
                variant="outlined"
                endIcon={<CustomArrowIcon open={dataSourceMenuOpen} />}
                sx={{
                    height: '35px',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    color: 'text.primary',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    minWidth: '130px',
                    px: '12px',
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(230, 230, 230, 1)',
                    ...(selectedDataSources.length > 0 && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: 'transparent', }),
                    ...(dataSourceMenuOpen && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: '#0064FF', }),
                    '&:hover': { borderColor: '#0064FF', backgroundColor: 'rgba(243, 248, 254, 1)', },
                }}
            > */}
                                <Box component="span" sx={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center' }}>
                    Data Source: 
                    <Box component="span" sx={{ fontWeight: 400, display: 'inline-flex', alignItems: 'center' }}>
                        {dataSourceButtonText}
                    </Box>
                </Box>
                {/* {dataSourceButtonText === 'Data Source' ? (
                    'Data Source'
                ) : (
                    <>
                        <Box component="span" sx={{ fontWeight: 500 }}>Data Source: </Box>
                        <Box component="span" sx={{ fontWeight: 400 }}>{dataSourceButtonText}</Box>
                    </>
                )} */}
            </Button>
            <Menu
                id="data-source-menu"
                anchorEl={dataSourceAnchorEl}
                open={dataSourceMenuOpen}
                onClose={handleDataSourceMenuClose}
                MenuListProps={{ disablePadding: true }}
                PaperProps={{ elevation: 0, sx: { width: 308, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mt: 1 } }}
            >
                <Box sx={{ p: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by Data Source name.."
                        value={dataSourceSearchTerm}
                        onChange={handleDataSourceSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <img
                                        src="/icons/search_icon.svg"
                                        alt="search"
                                        style={{ width: 20, height: 20, marginLeft: '8px' }}
                                    />
                                </InputAdornment>
                            ),
                        sx: { borderRadius: '10px', backgroundColor: ' rgba(0, 0, 0, 0.04)',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontSize: '14px', height: '35px',
                                '& ::placeholder': {
                                    fontFamily:'Inter',
                                    fontSize: '14px',
                                    fontWeight: '300',
                                    color: ' rgba(153, 153, 153, 1)',
                                    opacity: 1,
                                } }, }}
                    />
                </Box>
            <Divider />
                <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                    {Object.entries(dataSourcesByGroup).map(([groupName, sources]) => {
                        const filteredGroupSources = sources.filter(s => s.name.toLowerCase().includes(dataSourceSearchTerm.toLowerCase()));
                        if (filteredGroupSources.length === 0) return null;
                        const selectedInGroup = sources.filter(s => tempSelectedDataSources.includes(s.id));
                        const areAllInGroupSelected = selectedInGroup.length > 0 && selectedInGroup.length === sources.length;
                        const isGroupIndeterminate = selectedInGroup.length > 0 && !areAllInGroupSelected;
                        const isDisabled = !!activeDataSourceGroup && activeDataSourceGroup !== groupName;

                        return (
                            <Box key={groupName}>
                                <Box sx={{ backgroundColor: ' rgba(249, 250, 251, 1)', py: 1, px: 2 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            color: isDisabled ? 'text.disabled' : ' rgba(102, 102, 102, 1)',
                                        }}
                                    >
                                        {groupName}
                                    </Typography>
                                </Box>
                                <Box sx={{pt: 1}}>
                                    <FormControlLabel
                                        label="All"
                                        disabled={isDisabled}
                                        sx={{
                                            width: '100%',
                                            m: 0,
                                            backgroundColor: (areAllInGroupSelected || isGroupIndeterminate) ? 'rgba(243, 248, 254, 1)' : 'transparent',
                                            borderRadius: '4px',
                                            mb: 0.5,
                                            '& .MuiTypography-root': {
                                                fontSize: '14px' ,
                                                color: (areAllInGroupSelected || isGroupIndeterminate) ? '#333333' : 'rgba(102, 102, 102, 1)'
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                sx={{
                                                color: 'rgba(102, 102, 102, 1)',
                                                '&.Mui-checked': { color: '#0064FF' }
                                                }}
                                                checked={areAllInGroupSelected}
                                                indeterminate={isGroupIndeterminate}
                                                indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: '#0064FF' }} />}
                                                onChange={() => handleToggleDataSourceGroup(groupName, sources)}
                                            />
                                        }
                                    />
                                    {filteredGroupSources.map(source => (
                                        <Tooltip
                                            key={source.id}
                                            title={source.isSyncing ? "This data source is currently syncing. Cost data may be incomplete." : ""}
                                            placement="right"
                                            arrow
                                        >
                                            <div>
                                                <FormControlLabel
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <span>{source.name}</span>
                                                            {source.isSyncing && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        height: '20px',
                                                                        px: 1,
                                                                        borderRadius: '10px',
                                                                        backgroundColor: 'rgba(255, 193, 7, 0.2)',
                                                                        color: 'rgba(180, 120, 0)',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500,
                                                                    }}
                                                                >
                                                                    Syncing
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    }
                                                    disabled={isDisabled || source.isSyncing}
                                                            sx={{
                                                                width: '100%',
                                                                m: 0,
                                                                mb: 0.5,
                                                                backgroundColor: tempSelectedDataSources.includes(source.id) ? 'rgba(243, 248, 254, 1)' : 'transparent',
                                                                borderRadius: '4px',
                                                                '& .MuiTypography-root': {
                                                                fontSize: '14px' ,
                                                                color: tempSelectedDataSources.includes(source.id) ? '#333333' : 'rgba(102, 102, 102, 1)'
                                                                }
                                                            }}
                                                    control={
                                                <Checkbox
                                                    size="small"
                                                    sx={{
                                                        color: 'rgba(102, 102, 102, 1)',
                                                        '&.Mui-checked': { color: '#0064FF' }
                                                    }}
                                                    checked={tempSelectedDataSources.includes(source.id)}
                                                    onChange={() => handleToggleDataSource(source.id)}
                                                />
                                                    }
                                                />
                                            </div>
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent:'flex-end', alignItems: 'center', height: 40 }}>
                    <Button onClick={handleClearDataSources} sx={{ textTransform: 'none', color: ' #999999', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', marginRight:'-10px' }}>Clear</Button>
                    <Button onClick={handleApplyDataSources} sx={{ textTransform: 'none', color:'#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', marginRight:'8px'}}>Apply</Button>
                </Box>
            </Menu>

            <Button
                id="services-button"
                onClick={handleServicesMenuOpen}
                variant="outlined"
                endIcon={<CustomArrowIcon open={servicesMenuOpen} />}
                sx={{ height: '35px', textTransform: 'none', fontFamily: 'Inter', color: 'text.primary', fontSize: '14px', fontWeight: 500, borderRadius: '8px', minWidth: '100px', px: '12px', backgroundColor: 'transparent', borderColor: 'rgba(230, 230, 230, 1)', ...(selectedServices.length > 0 && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: 'transparent', }), '&:hover': { borderColor: '#0064FF', backgroundColor: 'rgba(243, 248, 254, 1)', }, ...(servicesMenuOpen && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: '#0064FF', }), }}
            >
                {selectedServices.length === 0 ? (
                    'Services'
                ) : (
                    <>
                        <Box component="span" sx={{ fontWeight: 500 }}>Services: </Box>
                        <Box component="span" sx={{ fontWeight: 400 }}>
                            {selectedServices.length === allServices.length ? 'All' : `${selectedServices.length} selected`}
                        </Box>
                    </>
                )}
            </Button>
            <Menu
                id="services-menu" anchorEl={servicesAnchorEl} open={servicesMenuOpen} onClose={handleServicesMenuClose}
                MenuListProps={{ disablePadding: true }}
                PaperProps={{ elevation: 0, sx: { width: 300, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mt: 1 } }}
            >
                <Box sx={{ p: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by Service name.."
                        value={serviceSearchTerm}
                        onChange={handleServiceSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <img
                                        src="/icons/search_icon.svg"
                                        alt="Search icon"
                                        style={{ width: 20, height: 20, marginLeft: '8px' }}
                                    />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontSize: '14px', height: '35px',
                                '& ::placeholder': {
                                    fontFamily:'Inter',
                                    fontSize: '14px',
                                    fontWeight: '300',
                                    color: 'rgba(153, 153, 153, 1)',
                                    opacity: 1,
                                }
                            },
                        }}
                    />
                </Box>
                <Divider />
                <Box sx={{ maxHeight: 240, overflow: 'auto', py: 1 }}>
                    <FormControlLabel
                        label="All"
                        sx={{
                            width: '100%',
                            m: 0,
                            borderRadius: '4px',
                            mb: 0.5,
                            backgroundColor: (allServices.length > 0 && tempSelectedServices.length === allServices.length) || (tempSelectedServices.length > 0 && tempSelectedServices.length < allServices.length) ? 'rgba(243, 248, 254, 1)' : 'transparent',
                            '& .MuiTypography-root': {
                                fontSize: '14px' ,
                                color: tempSelectedServices.length > 0 ? '#333333' : 'rgba(102, 102, 102, 1)'
                            }
                        }}
                        control={ <Checkbox
                            size="small"
                            sx={{
                                color: 'rgba(102, 102, 102, 1)',
                                '&.Mui-checked': { color: '#0064FF' }
                            }}
                            checked={allServices.length > 0 && tempSelectedServices.length === allServices.length} indeterminate={tempSelectedServices.length > 0 && tempSelectedServices.length < allServices.length} indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: '#0064FF' }} />} onChange={handleToggleAllServices} /> }
                    />
                    {filteredServices.map((service) => (
                        <FormControlLabel
                            key={service} label={service}
                            sx={{
                                width: '100%',
                                m: 0,
                                mb: 0.5,
                                borderRadius: '4px',
                                backgroundColor: tempSelectedServices.includes(service) ? 'rgba(243, 248, 254, 1)' : 'transparent',
                                '& .MuiTypography-root': {
                                    fontSize: '14px' ,
                                    color: tempSelectedServices.includes(service) ? '#333333' : 'rgba(102, 102, 102, 1)'
                                }
                            }}
                            control={ <Checkbox
                                size="small"
                                sx={{
                                    color: 'rgba(102, 102, 102, 1)',
                                    '&.Mui-checked': { color: '#0064FF' }
                                }}
                                checked={tempSelectedServices.includes(service)} onChange={() => handleToggleService(service)} /> }
                        />
                    ))}
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent:'flex-end', alignItems: 'center', height: 40}}>
                    <Button onClick={handleClearServices} sx={{ textTransform: 'none', color: ' #999999', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', marginRight:'-10px' }}>Clear</Button>
                    <Button onClick={handleApplyServices} sx={{ textTransform: 'none', color:'#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px', marginRight:'8px'}}>Apply</Button>
                </Box>
            </Menu>

            <Button
                id="label-filter-button"
                onClick={handleLabelFilterClick}
                variant="outlined"
                endIcon={<CustomArrowIcon open={isLabelFilterOpen} />}
                sx={{
                    height: '35px',
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    color: 'text.primary',
                    fontSize: '14px',
                    fontWeight: 500,
                    borderRadius: '8px',
                    minWidth: '80px',
                    px: '12px',
                    backgroundColor: 'transparent',
                    borderColor: 'rgba(230, 230, 230, 1)',
                    ...(labelFilters.length > 0 && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: 'transparent' }),
                    '&:hover': { borderColor: '#0064FF', backgroundColor: 'rgba(243, 248, 254, 1)' },
                    ...(isLabelFilterOpen && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: '#0064FF' }),
                }}
            >
                {labelFilters.length === 0 ? (
                    'Label'
                ) : (
                    <>
                        <Box component="span" sx={{ fontWeight: 500 }}>Label: </Box>
                        <Box component="span" sx={{ fontWeight: 400 }}>
                            {`${labelFilters.length} selected`}
                        </Box>
                    </>
                )}
            </Button>

            {activeDynamicFilters.map((filterName) => {
                const state = dynamicFilterState[filterName] || { selected: [], tempSelected: [], searchTerm: '' };
                const selectedCount = state.selected?.length || 0;
                const options = dynamicFilterOptions[filterName] || [];
                const searchTerm = state.searchTerm || '';
                const filteredOptions = options.filter((opt: string) =>
                    opt.toLowerCase().includes(searchTerm.toLowerCase())
                );

                return (
                    <React.Fragment key={filterName}>
                        <Button
                            onClick={(e) => handleDynamicMenuOpen(e, filterName)}
                            variant="outlined"
                            endIcon={<CustomArrowIcon open={activeDynamicFilterMenu === filterName} />}
                            sx={{
                                height: '35px',
                                textTransform: 'none',
                                fontFamily: 'Inter',
                                color: 'text.primary',
                                fontSize: '14px',
                                fontWeight: 500,
                                borderRadius: '8px',
                                minWidth: '10px',
                                px: '12px',
                                backgroundColor: 'transparent',
                                borderColor: 'rgba(230, 230, 230, 1)',
                                ...(selectedCount > 0 && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: 'transparent' }),
                                ...(activeDynamicFilterMenu === filterName && { backgroundColor: 'rgba(243, 248, 254, 1)', borderColor: '#0064FF' }),
                            }}
                        >
                            {selectedCount > 0 ? (
                                <>
                                    <Box component="span" sx={{ fontWeight: 500 }}>{filterName}: </Box>
                                    <Box component="span" sx={{ fontWeight: 400 }}>{`${selectedCount} selected`}</Box>
                                </>
                            ) : (
                                filterName
                            )}
                        </Button>
                        <Menu
                            anchorEl={dynamicFilterAnchorEl}
                            open={activeDynamicFilterMenu === filterName}
                            onClose={handleDynamicMenuClose}
                            MenuListProps={{ disablePadding: true }}
                            PaperProps={{ elevation: 0, sx: { width: 300, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', mt: 1 } }}
                        >
                            <Box sx={{ p: 2 }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder={`Search by ${filterName}...`}
                                    value={searchTerm}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDynamicFilterSearchChange(e, filterName)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <img
                                                    src="/icons/search_icon.svg"
                                                    alt="search"
                                                    style={{ width: 20, height: 20, marginLeft: '8px' }}
                                                />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: '10px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                            fontSize: '14px',
                                            height: '35px',
                                            '& ::placeholder': {
                                                fontFamily: 'Inter',
                                                fontSize: '14px',
                                                fontWeight: '300',
                                                color: 'rgba(153, 153, 153, 1)',
                                                opacity: 1,
                                            }
                                        },
                                    }}
                                />
                            </Box>
                            <Divider />
                            <Box sx={{ maxHeight: 200, overflow: 'auto', py: 1 }}>
                                {filteredOptions.map(item => (
                                    <FormControlLabel
                                        key={item}
                                        label={item}
                                        sx={{
                                            width: '100%',
                                            m: 0,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: '4px',
                                            mb: 0.5,
                                            backgroundColor: state.tempSelected?.includes(item) ? 'rgba(243, 248, 254, 1)' : 'transparent',
                                            '& .MuiTypography-root': {
                                                fontSize: '14px',
                                                color: state.tempSelected?.includes(item) ? 'rgba(51, 51, 51, 1)' : 'rgba(102, 102, 102, 1)',
                                            }
                                        }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={state.tempSelected?.includes(item) || false}
                                                onChange={() => handleToggleDynamicFilterItem(item, filterName)}
                                                sx={{ '&.Mui-checked': { color: '#0064FF' }, color: 'rgba(102, 102, 102, 1)' }}
                                            />
                                        }
                                    />
                                ))}
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: '4px 12px' }}>
                                <Button onClick={() => handleClearDynamicFilter(filterName)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Clear</Button>
                                <Button onClick={() => handleApplyDynamicFilter(filterName)} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                            </Box>
                        </Menu>
                    </React.Fragment>
                );
            })}

            <LabelFilterMenu
                open={isLabelFilterOpen}
                anchorEl={labelFilterAnchorEl}
                onClose={() => setLabelFilterAnchorEl(null)}
                onApply={setLabelFilters}
                labelKeys={filterOptions.labelKeys || []}
                labelValues={labelValueOptions}
                onFetchLabelValues={handleFetchLabelValues}
                appliedFilters={labelFilters}
                loadingLabelKey={loadingLabelKey} 
            />
            
            <IconButton
                onClick={handleAddFilterClick}
                sx={{ p: '2px' }}>
                <img src={`/icons/add-filter-icon.svg`} alt="Add icon" style={{ width: 32, height: 32 }} />
            </IconButton>
            <Menu
                anchorEl={addFilterAnchorEl}
                open={addFilterMenuOpen}
                onClose={handleAddFilterClose}
                MenuListProps={{ disablePadding: true }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        mt: 1,
                        width: 240,
                    },
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box>
                    <Typography sx={{ py: 2, px: 2, fontWeight: 600, fontSize: '14px', color: 'rgba(102, 102, 102, 1)' }}>Add More Filters</Typography>
                    <Divider />
                    {availableMoreFilters.length > 0 ? (
                        <Box sx={{ p: 0 }}>
                            {availableMoreFilters.map((filter) => (
                                <MenuItem key={filter} onClick={() => handleAddFilter(filter)} sx={{ borderRadius: '0px',height:50 }} >
                                    <Typography sx={{ width: '100%', fontSize: '16px',color:' rgba(102, 102, 102, 1)' }}>{filter}</Typography>
                                </MenuItem>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', p: 3, height:250, mt:5}}>
                            <NoSavedFiltersIcon />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                                No more filters
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, px: 1 }}>
                                You have added all available filter types.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Menu>

            <Divider orientation="vertical" sx={{ height: 30, alignSelf: 'center' }} />

            <Button
                onClick={handleClearAllFilters}
                sx={{
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    color: !isDefaultState ? '#0064FF' : 'rgba(153, 153, 153, 1)',
                    fontWeight: 500,
                }}
                disabled={isDefaultState} > Clear Filter
            </Button>

            <Button
                variant="contained"
                disableElevation
                onClick={handleSaveFilterClick}
                sx={{
                    textTransform: 'none',
                    fontFamily: 'Inter',
                    borderRadius: '8px',
                    fontWeight: 500,
                    bgcolor: 'rgba(0, 100, 255, 1)',
                    ml: 0,
                }}
            >
                Save Filter
            </Button>
            <SaveFilterDialog
                open={isSaveFilterDialogOpen}
                onClose={() => {
                    setIsSaveFilterDialogOpen(false);
                    setFilterToEdit(null);
                }}
                onSubmit={handleFilterSubmit}
                mode={dialogMode}
                initialFilterName={filterToEdit?.name || ''}
                filterToEditId={filterToEdit?.id}
                currentFilterState={getCurrentFilterState()}
                activeFilterName={filterToEdit?.name}
            />
            <SuccessToast
                open={toastOpen}
                message={toastMessage}
                onClose={handleCloseToast}
            />
        </Stack>
    );
};

// In /frontend/src/SideBarComponents/CostAnalysis/CostAnalysis.tsx
// FINAL, COMPLETE VERSION WITH RECONCILIATION LOGIC

// PASTE THIS OVER YOUR EXISTING `CostAnalysis` COMPONENT

// FINAL VERSION WITH CONTEXT SYNCING

// FINAL, VERIFIED VERSION WITH STABLE SYNC LOGIC
const CostAnalysis = ({ tenantId, dataSourceCount }: { tenantId: string, dataSourceCount?: number }) => {
    const navigate = useNavigate();
    const initialTenantIdRef = useRef(tenantId);

    const {
        isLoading: isPreloading,
        isLoaded: isPreloaded,
        initialCostData: preloadedData,
        error: preloadError,
        preloadedQueryVariables,
    } = useCostAnalysisContext();

    const [queryVariables, setQueryVariables] = useState<CostAnalysisInput | null>(null);
    const [activeGroupBy, setActiveGroupBy] = useState('Services');
    const [timeRangeSelection, setTimeRangeSelection] = useState('Last 30 days');
    const [dataSourceInfo, setDataSourceInfo] = useState<DataSourceOption[]>([]);
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    const isInitialFilterLoad = useRef(true);

    const showNoTenantState = !tenantId;
    const showEmptyDataSourceState = !showNoTenantState && dataSourceCount === 0;
    const showSingleSourceSyncingState =
        !showNoTenantState && !showEmptyDataSourceState &&
        dataSourceInfo.length === 1 && dataSourceInfo[0].isSyncing;

    const navigateToAddDataSource = () => {
        if (tenantId) navigate('/data-sources/add', { state: { tenantId: tenantId } });
    };

    const handleFiltersChange = useCallback((filters: any) => {
        const groupByMapping: { [key: string]: CostAnalysisGroupBy } = { 'Services': CostAnalysisGroupBy.service, 'Data Sources': CostAnalysisGroupBy.dataSourceId, 'Region': CostAnalysisGroupBy.region, 'Project': CostAnalysisGroupBy.projectId, 'SKU': CostAnalysisGroupBy.sku };
        const startDate = filters.customStartDate || dayjs().subtract(29, 'day');
        const endDate = filters.customEndDate || dayjs();
        const labelFilters: LabelFilter[] = (filters.labelFilters || []).flatMap((pair: LabelFilterPair) => pair.values.map(val => ({ key: pair.key, value: val })));
        const newVariables: CostAnalysisInput = {
            tenantId: tenantId,
            groupBy: groupByMapping[filters.groupBy] || CostAnalysisGroupBy.service,
            dateRange: { start: startDate.format('YYYY-MM-DD'), end: endDate.format('YYYY-MM-DD') },
            filters: {
                dataSourceIds: filters.selectedDataSources,
                services: filters.selectedServices,
                regions: filters.dynamicFilterState['Region']?.selected || [],
                skus: filters.dynamicFilterState['SKU']?.selected || [],
                projectIds: filters.dynamicFilterState['Project']?.selected || [],
                labels: labelFilters.length > 0 ? labelFilters : undefined,
            }
        };

        if (isInitialFilterLoad.current) {
            isInitialFilterLoad.current = false;
            if (isPreloaded && preloadedQueryVariables) {
                const currentFilterState = { groupBy: newVariables.groupBy, filters: newVariables.filters };
                const preloadedFilterState = { groupBy: preloadedQueryVariables.groupBy, filters: preloadedQueryVariables.filters };
                if (JSON.stringify(currentFilterState) !== JSON.stringify(preloadedFilterState)) {
                    console.log("[CostAnalysis] Stale preload data detected. Fetching live data for user's saved filters.");
                    setUserHasInteracted(true);
                }
            }
        } else {
            setUserHasInteracted(true);
        }
        setQueryVariables(newVariables);
        setActiveGroupBy(filters.groupBy);
        setTimeRangeSelection(filters.timeRangeSelection);
    }, [tenantId, isPreloaded, preloadedQueryVariables]);
    
    const { loading: liveLoading, error: liveError, data: liveData } = useQuery(GET_COST_ANALYSIS, {
        variables: { input: queryVariables },
        skip: !queryVariables || !userHasInteracted || showNoTenantState || showEmptyDataSourceState || showSingleSourceSyncingState,
    });
    
    const usePreloadedState = isPreloaded && !userHasInteracted;
    const isLoading = usePreloadedState ? false : (isPreloading || liveLoading);
    const data = usePreloadedState ? preloadedData : liveData;
    const error = usePreloadedState ? preloadError : liveError;
    
    const formatCurrency = (value: number | null | undefined) => { if (value === null || value === undefined) return '$0.00'; return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
    const formatPercentage = (value: number | null | undefined) => { if (value === null || value === undefined) return '+0.00%'; const sign = value > 0 ? '+' : ''; return `${sign}${value.toFixed(2)}%`; };
    const comparisonText = useMemo(() => { switch (timeRangeSelection) { case 'Today': return 'vs yesterday'; case 'Last 7 days': return 'vs previous 7 days'; case 'Last 15 days': return 'vs previous 15 days'; case 'Last 30 days': return 'vs previous 30 days'; case 'Custom': return 'vs previous period'; default: return 'vs last month'; } }, [timeRangeSelection]);
    const getSingularLabel = (label: string) => { if (label === 'Data Sources') return 'Data Source'; if (label.endsWith('s')) { return label.slice(0, -1); } return label; };
    
    const kpis = data?.getCostAnalysis?.kpis;
    const breakdown = data?.getCostAnalysis?.breakdown || [];

    const { augmentedTimeSeries, colorMap } = useMemo(() => {
        const allGroups = new Set<string>();
        const existingBreakdown = data?.getCostAnalysis?.breakdown || [];
        const existingTimeSeries = data?.getCostAnalysis?.timeSeries || [];
        existingBreakdown.forEach((item: BreakdownRow) => allGroups.add(item.group));
        existingTimeSeries.forEach((dataPoint: TimeSeriesDataPoint) => { dataPoint.groups.forEach((group: TimeSeriesGroup) => allGroups.add(group.name)); });
        const newColorMap = createColorMap(Array.from(allGroups));
        if (!queryVariables) { return { augmentedTimeSeries: [], colorMap: newColorMap }; }
        let selectedDays = 30;
        switch (timeRangeSelection) { case 'Last 7 days': selectedDays = 7; break; case 'Last 15 days': selectedDays = 15; break; case 'Today': selectedDays = 1; break; case 'Custom': const start = dayjs(queryVariables.dateRange.start); const end = dayjs(queryVariables.dateRange.end); selectedDays = end.diff(start, 'day') + 1; break; default: selectedDays = 30; }
        const totalDays = Math.max(selectedDays, 30);
        let finalTimeSeries = [...existingTimeSeries];
        const daysToAdd = totalDays - finalTimeSeries.length;
        if (finalTimeSeries.length > 0 && daysToAdd > 0) { const lastDate = dayjs(finalTimeSeries[finalTimeSeries.length - 1].date); for (let i = 1; i <= daysToAdd; i++) { const nextDate = lastDate.add(i, 'day').format('YYYY-MM-DD'); finalTimeSeries.push({ date: nextDate, groups: [], }); } }
        return { augmentedTimeSeries: finalTimeSeries, colorMap: newColorMap };
    }, [data, queryVariables, timeRangeSelection]);

    return (
        <Box sx={{ p: 3, backgroundColor: '#F9FAFB', fontFamily: 'Inter', position: 'relative' }}>
            {showNoTenantState && <NoTenantEmptyState />}
            {showEmptyDataSourceState && <EmptyStateOverlay onNavigate={navigateToAddDataSource} />}
    
            <Typography variant="subtitle2" component="h2" sx={{ fontFamily: 'Inter', fontWeight: 600, fontSize: '14px', color: '#333333', mt: 0 }}>
                Cost Analysis
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '12px', color: '#666666', mb: 2 }}>
                Analyze cost trends and discover savings opportunities.
            </Typography>
            <Paper sx={{ p: 1.5, mb: 2, borderRadius: '10px', boxShadow: 'none', backgroundColor: 'rgba(255, 255, 255, 1)', minHeight: '54px', width: '100%', maxWidth: '1300px', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
                <FilterBar
                    onFiltersChange={handleFiltersChange}
                    tenantId={tenantId}
                    initialTenantId={initialTenantIdRef.current}
                    onDataSourceInfoChange={setDataSourceInfo}
                />
            </Paper>
    
            {showSingleSourceSyncingState ? (
                <CenteredMessage
                    imgSrc="/icons/syncing.svg"
                    title="Your data source is syncing"
                    message="We're currently processing your cost data. This may take a few moments. Please check back soon to see your complete cost analysis."
                />
            ) : isLoading ? (
                <Paper sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', mt: 2, borderRadius: '16px', boxShadow: 'none', background: 'transparent' }}>
                    <CircularProgress />
                </Paper>
            ) : error ? (
                <UserFriendlyError message={getFriendlyErrorMessage(error)} />
            ) : data && (data.getCostAnalysis?.breakdown?.length > 0 || data.getCostAnalysis?.timeSeries?.length > 0) ? (
                <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} sx={{ alignItems: 'stretch' }} >
                        <SummaryCard
                            title="Total Cost"
                            amount={formatCurrency(kpis?.totalCost)}
                            change={formatPercentage(kpis?.totalCostChangePercentage)}
                            changeType={(kpis?.totalCostChangePercentage ?? 0) >= 0 ? 'increase' : 'decrease'}
                            credits={formatCurrency(kpis?.totalCredits)}
                            comparisonText={comparisonText}
                            sx={{ backgroundImage: 'url(/textures/1st-card.svg)', backgroundRepeat: 'no-repeat', backgroundPosition: 'top right', backgroundSize: '300px' }}
                        />
                        <SummaryCard
                            title="Total Forecasted Cost"
                            amount={formatCurrency(kpis?.totalForecastedCost)}
                            change={formatPercentage(kpis?.totalForecastedCostChangePercentage)}
                            changeType={(kpis?.totalForecastedCostChangePercentage ?? 0) >= 0 ? 'increase' : 'decrease'}
                            comparisonText="vs last month"
                            sx={{ backgroundImage: 'url(/textures/2nd-card.svg)', backgroundRepeat: 'no-repeat', backgroundPosition: 'top right', backgroundSize: '300px' }}
                        />
                        <SummaryCard
                            title={`Top ${getSingularLabel(activeGroupBy)}`}
                            entityName={kpis?.topGroup || 'N/A'}
                            amount={formatCurrency(kpis?.topGroupCost)}
                            change={formatPercentage(kpis?.topGroupCostChangePercentage)}
                            changeType={(kpis?.topGroupCostChangePercentage ?? 0) >= 0 ? 'increase' : 'decrease'}
                            credits={formatCurrency(kpis?.topGroupCredits)}
                            comparisonText={comparisonText}
                            sx={{ backgroundImage: 'url(/textures/4th-card.svg)', backgroundRepeat: 'no-repeat', backgroundPosition: 'top right', backgroundSize: '300px' }}
                        />
                    </Stack>
                    <Box mb={2}>
                        <CostOverTimeChart timeSeries={augmentedTimeSeries} colorMap={colorMap} />
                    </Box>
                    <CostBreakdownTable
                        breakdown={breakdown}
                        groupBy={activeGroupBy}
                        colorMap={colorMap}
                    />
                </>
            ) : (
                <CenteredMessage
                    imgSrc="/icons/no-results-found.svg"
                    title="Sorry! No results found"
                    message="Try adjusting your filters or search terms and try again."
                />
            )}
        </Box>
    );
};

export default CostAnalysis;